import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { authService } from '../services/authService';
import { Eye, EyeOff } from 'lucide-react';

const LoginPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Login';
  }, []);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      // First, validate credentials and get verification code sent
      const response = await authService.login(data.email, data.password);
      
      // Check if email was sent successfully or if code is provided
      if (response.code) {
        // Email failed, but code is provided in response
        toast.success(`Verification code: ${response.code}`, { duration: 10000 });
        console.log('Verification Code:', response.code);
      } else {
        toast.success('Verification code sent to your email!');
      }
      
      // Navigate to verification page with email
      navigate('/auth/verification', { 
        state: { email: data.email } 
      });
    } catch (error) {
      toast.error(error.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left Side - Full Screen Image (45%) */}
      <div className="w-[45%] relative overflow-hidden">
        <img
          src="/login_side_image.png"
          alt="Healthcare Professional"
          className="absolute top-0 left-0 w-full h-[115vh] object-cover"
        />
      </div>

      {/* Right Side - Login Form (55%) */}
      <div className="w-[55%] flex items-start justify-center bg-white pt-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-xs w-full space-y-6">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-blue-600 mb-4">
              Welcome To
            </h1>
            
            {/* Medi Fusion Logo */}
            <div className="flex justify-center mb-4">
              <img
                src="/login_medi_logo.png"
                alt="Medi Fusion"
                className="h-16 w-auto"
              />
            </div>
            
            {/* Account Creation Link */}
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link
                to="/auth/register"
                className="text-blue-600 hover:text-blue-500 font-medium"
              >
                Create one here.
              </Link>
            </p>
          </div>

          {/* Login Form */}
          <div className="space-y-6">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                          {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email:
              </label>
              <input
                id="email"
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Please enter a valid email address',
                  },
                })}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your email"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password:
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    {...register('password', {
                      required: 'Password is required',
                    })}
                    className="w-full px-3 py-1.5 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              {/* Log In Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-1.5 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mx-auto"></div>
                ) : (
                  'Log In'
                )}
              </button>
            </form>

            {/* Forgot Password Link */}
            <div className="text-center">
              <Link
                to="/auth/forgot-password"
                className="text-blue-600 hover:text-blue-500 text-sm font-medium"
              >
                Forgot Password?
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 